import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef
} from "react";

import { usePlayerStore } from "../../store/usePlayerStore";

const keyboardTimeout = 1000;

const useVolume = ({
  audio,
  setShowVolumeSlider
}: {
  audio?: HTMLMediaElement;
  setShowVolumeSlider: Dispatch<SetStateAction<boolean>>;
}) => {
  const { useStoreState } = usePlayerStore();
  const player = useStoreState.player();

  const setIsMuted = useStoreState.setIsMuted();
  const setVolume = useStoreState.setVolume();

  const volumeSliderTimer = useRef<number>();
  const keyboardVolumeChange = useCallback(
    (increase: boolean) => {
      if (audio) {
        const newVolume = +Math.min(
          1,
          Math.max(0, (audio.volume ?? 0) + (increase ? 0.05 : -0.05))
        ).toFixed(3);

        setShowVolumeSlider(true);
        player.setVolume(newVolume, true);

        clearTimeout(volumeSliderTimer.current);

        volumeSliderTimer.current = window.setTimeout(
          () => setShowVolumeSlider(false),
          keyboardTimeout
        );
      }
    },
    [audio, player, setShowVolumeSlider]
  );

  useEffect(() => {
    const events = [
      {
        event: "volumechange",
        fn: event => {
          const target = event.target as HTMLAudioElement;
          setIsMuted(target.muted);
          setVolume(target.volume);
        }
      }
    ];

    events.forEach(({ event, fn }) => audio?.addEventListener(event, fn));

    return () => {
      events.forEach(({ event, fn }) => audio?.removeEventListener(event, fn));
    };
  }, [audio, setIsMuted, setVolume]);

  return {
    keyboardVolumeChange
  };
};

export default useVolume;
